import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { modules, MODULE_CATEGORY_LABELS, getModulesByCategory, calcUsedPoints } from "@/data/modules";
import { plans } from "@/data/plans";
import { Pencil, ExternalLink, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

interface Profile {
  id: string;
  first_name: string | null;
  last_name: string | null;
  company: string | null;
  created_at: string;
}

interface Sub {
  user_id: string;
  plan_id: string;
  status: string;
  stripe_customer_id: string | null;
}

interface UserModule {
  user_id: string;
  module_id: string;
}

interface Order {
  user_id: string;
  email: string | null;
}

export default function AdminCustomers() {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [subs, setSubs] = useState<Sub[]>([]);
  const [userModules, setUserModules] = useState<UserModule[]>([]);
  const [emails, setEmails] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  // Module edit dialog state
  const [editUserId, setEditUserId] = useState<string | null>(null);
  const [editModules, setEditModules] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    const [{ data: p }, { data: s }, { data: m }, { data: o }] = await Promise.all([
      supabase.from("profiles").select("*").order("created_at", { ascending: false }),
      supabase.from("subscriptions").select("user_id, plan_id, status, stripe_customer_id"),
      supabase.from("user_modules").select("user_id, module_id"),
      supabase.from("orders").select("user_id, email"),
    ]);
    setProfiles((p as Profile[]) ?? []);
    setSubs((s as Sub[]) ?? []);
    setUserModules((m as UserModule[]) ?? []);

    // Build email map from orders
    const emailMap: Record<string, string> = {};
    (o ?? []).forEach((order: Order) => {
      if (order.email) emailMap[order.user_id] = order.email;
    });
    setEmails(emailMap);
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const getModuleName = (id: string) => modules.find((m) => m.id === id)?.name ?? id;

  const openEditModules = (userId: string) => {
    const current = userModules.filter((m) => m.user_id === userId).map((m) => m.module_id);
    setEditModules(current);
    setEditUserId(userId);
  };

  const toggleModule = (moduleId: string) => {
    setEditModules((prev) =>
      prev.includes(moduleId) ? prev.filter((id) => id !== moduleId) : [...prev, moduleId]
    );
  };

  // Points budget for current edit user
  const editUserSub = editUserId ? subs.find((s) => s.user_id === editUserId) : null;
  const editUserPlan = editUserSub ? plans.find((p) => p.id === editUserSub.plan_id) : null;
  const pointsBudget = editUserPlan?.pointsIncluded ?? 0;
  const pointsUsed = calcUsedPoints(editModules);
  const isOverBudget = editUserPlan?.pointsIncluded !== null && pointsUsed > pointsBudget;

  const saveModules = async (force = false) => {
    if (!editUserId) return;
    if (isOverBudget && !force) return;
    setSaving(true);
    try {
      // Delete existing modules for this user
      await supabase.from("user_modules").delete().eq("user_id", editUserId);

      // Insert new modules
      if (editModules.length > 0) {
        const rows = editModules.map((module_id) => ({
          user_id: editUserId,
          module_id,
        }));
        const { error } = await supabase.from("user_modules").insert(rows);
        if (error) throw error;
      }

      toast.success("Module aktualisiert");
      setEditUserId(null);
      await fetchData();
    } catch (e: any) {
      toast.error("Fehler: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" /></div>;
  }

  const modulesByCategory = getModulesByCategory();

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Kunden</h1>
      {profiles.length === 0 ? (
        <p className="text-muted-foreground">Keine Kunden vorhanden.</p>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>E-Mail</TableHead>
                <TableHead>Firma</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Module</TableHead>
                <TableHead>Registriert</TableHead>
                <TableHead>Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {profiles.map((p) => {
                const sub = subs.find((s) => s.user_id === p.id);
                const mods = userModules.filter((m) => m.user_id === p.id);
                const name = [p.first_name, p.last_name].filter(Boolean).join(" ") || "—";
                const email = emails[p.id];
                return (
                  <TableRow key={p.id}>
                    <TableCell>{name}</TableCell>
                    <TableCell className="text-xs">{email || "—"}</TableCell>
                    <TableCell>{p.company || "—"}</TableCell>
                    <TableCell>
                      {sub ? <Badge variant="secondary">{sub.plan_id}</Badge> : <span className="text-muted-foreground text-xs">Kein Plan</span>}
                    </TableCell>
                    <TableCell>
                      {sub ? (
                        <Badge className={sub.status === "active" || sub.status === "trialing" ? "bg-green-600 text-white" : sub.status === "canceled" ? "bg-destructive text-destructive-foreground" : ""}>
                          {sub.status}
                        </Badge>
                      ) : "—"}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {mods.length > 0
                          ? mods.map((m) => <Badge key={m.module_id} variant="outline" className="text-xs">{getModuleName(m.module_id)}</Badge>)
                          : <span className="text-muted-foreground text-xs">Keine</span>}
                      </div>
                    </TableCell>
                    <TableCell>{new Date(p.created_at).toLocaleDateString("de-DE")}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => openEditModules(p.id)} title="Module bearbeiten">
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        {sub?.stripe_customer_id && (
                          <Button size="sm" variant="outline" asChild title="In Stripe öffnen">
                            <a href={`https://dashboard.stripe.com/customers/${sub.stripe_customer_id}`} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Module Edit Dialog */}
      <Dialog open={!!editUserId} onOpenChange={(open) => !open && setEditUserId(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Module bearbeiten</DialogTitle>
            <DialogDescription>
              {editUserPlan
                ? `Plan: ${editUserPlan.name} · Budget: ${editUserPlan.pointsIncluded === null ? "Unbegrenzt" : `${editUserPlan.pointsIncluded} Punkte`}`
                : "Kein aktiver Plan"}
            </DialogDescription>
          </DialogHeader>

          {/* Points counter */}
          <div className={`flex items-center justify-between rounded-lg px-4 py-3 text-sm font-semibold ${
            isOverBudget ? "bg-destructive/10 text-destructive border border-destructive/30" : "bg-primary/10 text-primary border border-primary/20"
          }`}>
            <span>Verbrauchte Punkte</span>
            <span>{pointsUsed} / {editUserPlan?.pointsIncluded === null ? "∞" : pointsBudget}</span>
          </div>
          {isOverBudget && (
            <div className="flex items-center gap-2 text-sm text-destructive">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span>Budget um {pointsUsed - pointsBudget} Punkt{pointsUsed - pointsBudget !== 1 ? "e" : ""} überschritten!</span>
            </div>
          )}

          <div className="space-y-4 py-2">
            {Object.entries(modulesByCategory).map(([catKey, catModules]) => (
              <div key={catKey}>
                <h4 className="text-sm font-semibold text-muted-foreground mb-2">
                  {MODULE_CATEGORY_LABELS[catKey as keyof typeof MODULE_CATEGORY_LABELS]}
                </h4>
                <div className="space-y-1.5">
                  {catModules.map((mod) => (
                    <label key={mod.id} className="flex items-center gap-2 cursor-pointer hover:bg-muted/50 rounded px-2 py-1">
                      <Checkbox
                        checked={editModules.includes(mod.id)}
                        onCheckedChange={() => toggleModule(mod.id)}
                      />
                      <span className="text-sm">{mod.name}</span>
                      <Badge variant="outline" className="text-xs ml-auto">{mod.points}P</Badge>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter className="flex-col gap-2 sm:flex-col">
            <div className="flex gap-2 w-full justify-end">
              <Button variant="outline" onClick={() => setEditUserId(null)}>Abbrechen</Button>
              <Button onClick={() => saveModules(false)} disabled={saving || isOverBudget}>
                {saving ? "Speichern…" : "Speichern"}
              </Button>
            </div>
            {isOverBudget && (
              <Button
                variant="destructive"
                className="w-full"
                onClick={() => saveModules(true)}
                disabled={saving}
              >
                {saving ? "Speichern…" : "Trotzdem speichern (erzwingen)"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
